import React, { useState } from 'react';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Upload, 
  Download, 
  FileText, 
  Users, 
  Truck, 
  Building2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Package
} from 'lucide-react';

const Import = () => {
  const [activeTab, setActiveTab] = useState('chauffeurs');
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const handleFileChange = (e, entityType) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreviewData(null);
      handlePreview(selectedFile, entityType);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e, entityType) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      setPreviewData(null);
      handlePreview(droppedFile, entityType);
    }
  };

  const handlePreview = async (selectedFile, entityType) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('entity_type', entityType);

      const response = await api.post('/import/preview', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setPreviewData(response.data);
      
      const stats = response.data.stats;
      if (stats.errors > 0) {
        toast.error(`${stats.errors} erreur(s) détectée(s)`);
      } else if (stats.warnings > 0) {
        toast.warning(`${stats.warnings} doublon(s) détecté(s)`);
      } else {
        toast.success(`${stats.valid} ligne(s) valide(s)`);
      }
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async (entityType) => {
    try {
      const response = await api.get(`/import/template/${entityType}`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `template_${entityType}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template téléchargé');
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleDownloadSample = async (entityType) => {
    try {
      // Générer un CSV exemple avec des données de démonstration
      let sampleData = '';
      
      if (entityType === 'chauffeurs') {
        sampleData = `nom,telephone,telephone_2,email,pays,intra_inter_land,type_camion_1,capacite_1,nombre_camions_1,type_camion_2,capacite_2,nombre_camions_2
KOUAME Koffi,+225 01 02 03 04 05,+225 06 07 08 09 10,kouame@example.com,Côte d'Ivoire,Intraland,Camion fourgon,10,3,Camion bâché,15,2
TOURE Ibrahim,+223 20 21 22 23 24,,toure@example.com,Mali,Interland,Camion frigorifique,12,5,,
DIALLO Mamadou,+226 70 12 34 56 78,+226 71 23 45 67 89,diallo@example.com,Burkina Faso,Les deux,Semi-remorque bâché,40,1,Camion porte-conteneur,35,2`;
      } else if (entityType === 'vehicules') {
        sampleData = `immatriculation,pays,usage_principal,capacite_tonnes,marque,modele,numero_assurance
CI-1234-AB-CI,Côte d'Ivoire,Transport de marchandises,20.5,Mercedes,Actros,ASS123456
ML-5678-CD-ML,Mali,Mixte,15.0,Volvo,FH16,ASS789012
BF-9012-EF-BF,Burkina Faso,Transport de personnes,12.0,MAN,TGX,ASS345678`;
      } else if (entityType === 'partenaires') {
        sampleData = `nom,type_proprietaire,telephone,telephone_2,email,pays
SARL Transport Express,Entreprise ou société de transport,+225 27 20 30 40 50,+225 27 20 30 40 51,contact@transport-express.ci,Côte d'Ivoire
KONE Amadou,Personne physique,+223 76 12 34 56 78,+223 76 98 76 54 32,kone@example.com,Mali
OUEDRAOGO SA,Personne morale,+226 70 11 22 33 44,,ouedraogo@transport.bf,Burkina Faso`;
      } else if (entityType === 'operations') {
        sampleData = `Matricule,Date,Statut,Trajet,Transporteur,Chauffeur,Opérateur,Type camion,Tarif,Jours Immo,Coût Unit. Immo,Total Immo,Pays,vehicule_immatriculation,vehicule_type,vehicule_capacite_t
BC-OP-000123,2025-11-06 09:00,En cours,Abidjan -> Yopougon,Trans CI,Koné Moussa <+2250701020304>,operateur1@baobabco.net,fourgon,250000,0,0,0,Côte d'Ivoire,4270FH01,Fourgon,10
BC-OP-000124,2025-11-08 07:00,Planifiée,Abidjan|Accra,FanMilk GH,Mensah Kofi <0023307000000>,operateur1@baobabco.net,frigo,850000,1,250000,,Ghana,GH1234AB,Frigo,15
BC-OP-000125,2025-11-10 06:30,En cours,San Pedro-Daloa,OLAM,Tra Bi <0703040506>,operateur1@baobabco.net,plateau,450000,2,,500000,Côte d'Ivoire,CI9876XY,Plateau,12`;
      }
      
      const blob = new Blob([sampleData], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `exemple_${entityType}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Exemple téléchargé avec succès');
    } catch (error) {
      toast.error('Erreur lors du téléchargement de l\'exemple');
    }
  };

  const handleImport = async () => {
    if (!previewData) return;
    
    if (previewData.stats.errors > 0) {
      toast.error('Impossible d\'importer: il y a des erreurs');
      return;
    }
    
    if (!confirm(`Importer ${previewData.stats.valid} ligne(s) ? Les doublons seront ignorés.`)) {
      return;
    }
    
    try {
      setImporting(true);
      const response = await api.post('/import/execute', {
        entity_type: previewData.entity_type,
        rows_data: previewData.rows
      });
      
      toast.success(`✓ ${response.data.imported} ligne(s) importée(s), ${response.data.skipped} ignorée(s)`);
      
      // Reset
      setFile(null);
      setPreviewData(null);
      
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setImporting(false);
    }
  };

  const handleOperationsImport = async () => {
    if (!file) return;
    
    if (!confirm(`Importer les opérations depuis ${file.name} ?`)) {
      return;
    }
    
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/operations/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const { summary, errors, warnings } = response.data;
      
      // Afficher résultats
      if (summary.failed > 0) {
        toast.error(`❌ ${summary.failed} ligne(s) échouée(s)`);
      } else {
        toast.success(`✓ ${summary.created} créée(s), ${summary.updated} mise(s) à jour`);
      }
      
      if (warnings.length > 0) {
        toast.warning(`⚠️ ${warnings.length} avertissement(s)`);
      }
      
      // Afficher détails
      setPreviewData({
        summary,
        errors,
        warnings,
        entity_type: 'operations'
      });
      
      // Reset file
      setFile(null);
      
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setImporting(false);
    }
  };

  const getRowColor = (row) => {
    if (row.errors && row.errors.length > 0) {
      return 'bg-red-50 border-red-200';
    }
    if (row.warnings && row.warnings.length > 0) {
      return 'bg-yellow-50 border-yellow-200';
    }
    return 'bg-green-50 border-green-200';
  };

  const getRowIcon = (row) => {
    if (row.errors && row.errors.length > 0) {
      return <XCircle className="h-5 w-5 text-red-600" />;
    }
    if (row.warnings && row.warnings.length > 0) {
      return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
    }
    return <CheckCircle className="h-5 w-5 text-green-600" />;
  };

  const renderUploadZone = (entityType, icon, title) => {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {icon}
              {title}
            </CardTitle>
            <CardDescription>
              Importez depuis un fichier CSV ou JSON
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => handleDownloadTemplate(entityType)}
                variant="outline"
                className="w-full"
              >
                <Download className="h-4 w-4 mr-2" />
                Template vide
              </Button>
              
              <Button
                onClick={() => handleDownloadSample(entityType)}
                variant="outline"
                className="w-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200"
              >
                <FileText className="h-4 w-4 mr-2" />
                Exemple avec données
              </Button>
            </div>
            
            <p className="text-xs text-gray-500 text-center">
              💡 Téléchargez l'exemple pour voir le format attendu avec des données réelles
            </p>

            <div
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, entityType)}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-emerald-500 transition-colors cursor-pointer"
            >
              <input
                type="file"
                accept=".csv,.json"
                onChange={(e) => handleFileChange(e, entityType)}
                className="hidden"
                id={`file-input-${entityType}`}
              />
              <label htmlFor={`file-input-${entityType}`} className="cursor-pointer">
                <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-lg font-medium text-gray-700">
                  Cliquez ou glissez votre fichier ici
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  CSV ou JSON (max 1000 lignes)
                </p>
              </label>
            </div>

            {file && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-gray-600" />
                  <span className="text-sm font-medium">{file.name}</span>
                </div>
                {loading && <RefreshCw className="h-5 w-5 animate-spin text-emerald-600" />}
              </div>
            )}
          </CardContent>
        </Card>

        {previewData && (
          <>
            {/* Statistics */}
            <Card>
              <CardHeader>
                <CardTitle>Statistiques de validation</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{previewData.stats.total}</p>
                    <p className="text-sm text-gray-600">Total</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{previewData.stats.valid}</p>
                    <p className="text-sm text-gray-600">Valides</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-yellow-600">{previewData.stats.warnings}</p>
                    <p className="text-sm text-gray-600">Doublons</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">{previewData.stats.errors}</p>
                    <p className="text-sm text-gray-600">Erreurs</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Preview Table */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Aperçu des données</CardTitle>
                <Button
                  onClick={handleImport}
                  disabled={previewData.stats.errors > 0 || importing}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {importing ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Import en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Importer {previewData.stats.valid - previewData.stats.warnings} ligne(s)
                    </>
                  )}
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {previewData.rows.map((row, idx) => (
                    <div
                      key={idx}
                      className={`border rounded-lg p-3 ${getRowColor(row)}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {getRowIcon(row)}
                          <span className="font-medium">Ligne {row.row_num}</span>
                        </div>
                        <div className="text-sm text-gray-600">
                          {Object.keys(row.data).length} champ(s)
                        </div>
                      </div>
                      
                      {/* Data preview */}
                      <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                        {Object.entries(row.data).slice(0, 4).map(([key, value]) => (
                          value && (
                            <div key={key}>
                              <span className="text-gray-600">{key}:</span>{' '}
                              <span className="font-medium">{String(value).substring(0, 30)}</span>
                            </div>
                          )
                        ))}
                      </div>

                      {/* Errors */}
                      {row.errors && row.errors.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {row.errors.map((error, i) => (
                            <p key={i} className="text-sm text-red-700 flex items-center gap-1">
                              <XCircle className="h-3 w-3" />
                              {error}
                            </p>
                          ))}
                        </div>
                      )}

                      {/* Warnings */}
                      {row.warnings && row.warnings.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {row.warnings.map((warning, i) => (
                            <p key={i} className="text-sm text-yellow-700 flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              {warning}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    );
  };

  const renderOperationsUploadZone = () => {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Import Opérations
            </CardTitle>
            <CardDescription>
              Importez vos opérations depuis un fichier CSV avec support des véhicules et dates/heures
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => handleDownloadTemplate('operations')}
                variant="outline"
                className="w-full"
              >
                <Download className="h-4 w-4 mr-2" />
                Template vide
              </Button>
              
              <Button
                onClick={() => handleDownloadSample('operations')}
                variant="outline"
                className="w-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200"
              >
                <FileText className="h-4 w-4 mr-2" />
                Exemple avec données
              </Button>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">✨ Fonctionnalités avancées</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• <strong>Idempotence</strong>: Re-importer le même fichier met à jour les opérations existantes</li>
                <li>• <strong>Véhicules</strong>: Rattachement automatique par immatriculation (création si absent)</li>
                <li>• <strong>Dates/Heures</strong>: Support des dates de chargement et déchargement</li>
                <li>• <strong>Calcul auto</strong>: Immobilisation calculée si un champ manque</li>
                <li>• <strong>Validation</strong>: Vérification de l'opérateur et cohérence des données</li>
              </ul>
            </div>

            <div
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'operations')}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-emerald-500 transition-colors cursor-pointer"
            >
              <input
                type="file"
                accept=".csv"
                onChange={(e) => handleFileChange(e, 'operations')}
                className="hidden"
                id="file-input-operations"
              />
              <label htmlFor="file-input-operations" className="cursor-pointer">
                <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-lg font-medium text-gray-700">
                  Cliquez ou glissez votre fichier CSV ici
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Format CSV uniquement
                </p>
              </label>
            </div>

            {file && (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-sm">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleOperationsImport}
                  disabled={importing}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {importing ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Import en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Importer
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Résultats */}
        {previewData && previewData.entity_type === 'operations' && (
          <Card>
            <CardHeader>
              <CardTitle>Résultats de l'import</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-700">{previewData.summary?.created || 0}</div>
                  <div className="text-sm text-green-600">Créées</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-700">{previewData.summary?.updated || 0}</div>
                  <div className="text-sm text-blue-600">Mises à jour</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-red-700">{previewData.summary?.failed || 0}</div>
                  <div className="text-sm text-red-600">Échecs</div>
                </div>
              </div>

              {/* Errors */}
              {previewData.errors && previewData.errors.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-red-700 mb-2">❌ Erreurs ({previewData.errors.length})</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {previewData.errors.map((err, idx) => (
                      <div key={idx} className="bg-red-50 border border-red-200 rounded p-3 text-sm">
                        <span className="font-medium text-red-900">Ligne {err.row}:</span>{' '}
                        <span className="text-red-700">{err.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Warnings */}
              {previewData.warnings && previewData.warnings.length > 0 && (
                <div>
                  <h4 className="font-semibold text-yellow-700 mb-2">⚠️ Avertissements ({previewData.warnings.length})</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {previewData.warnings.slice(0, 20).map((warn, idx) => (
                      <div key={idx} className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                        <span className="text-yellow-800">{warn.message}</span>
                      </div>
                    ))}
                    {previewData.warnings.length > 20 && (
                      <p className="text-sm text-gray-500 text-center">... et {previewData.warnings.length - 20} autre(s)</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Import de Données</h1>
        <p className="text-gray-600">Importez vos chauffeurs, partenaires, véhicules et opérations depuis CSV</p>
      </div>

      <Tabs value={activeTab} onValueChange={(val) => {
        setActiveTab(val);
        setFile(null);
        setPreviewData(null);
      }} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="chauffeurs">
            <Users className="h-4 w-4 mr-2" />
            Chauffeurs
          </TabsTrigger>
          <TabsTrigger value="partenaires">
            <Building2 className="h-4 w-4 mr-2" />
            Partenaires
          </TabsTrigger>
          <TabsTrigger value="vehicules">
            <Truck className="h-4 w-4 mr-2" />
            Véhicules
          </TabsTrigger>
          <TabsTrigger value="operations">
            <Package className="h-4 w-4 mr-2" />
            Opérations
          </TabsTrigger>
        </TabsList>

        <TabsContent value="chauffeurs">
          {renderUploadZone('chauffeurs', <Users className="h-5 w-5" />, 'Import Chauffeurs')}
        </TabsContent>

        <TabsContent value="partenaires">
          {renderUploadZone('partenaires', <Building2 className="h-5 w-5" />, 'Import Partenaires')}
        </TabsContent>

        <TabsContent value="vehicules">
          {renderUploadZone('vehicules', <Truck className="h-5 w-5" />, 'Import Véhicules')}
        </TabsContent>

        <TabsContent value="operations">
          {renderOperationsUploadZone()}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Import;
